/**
 * F298789 / US2069694: decides, per grid column, whether it can be priced from a lender program or has to
 * fall back to user-program-quotes, and whether the grid is complete enough to render. Redux-free by design -
 * callers pass in whether a term has a program and whether a cell priced, so the rules can be tested alone.
 */

/** Which calculation engine produced (or must produce) a cell's payment. */
export enum PaymentGridSource {
    ProgramQuotes = 'programQuotes',
    UserProgramQuotes = 'userProgramQuotes',
    /** Neither engine could price the column: shown blank with a validation message, and no longer holding the render gate. */
    Unavailable = 'unavailable'
}

/**
 * Keyed on `UserProgramQuotes` alone. `Unavailable` is deliberately excluded: that column shows no payment at
 * all, so calling it "Calculated" would claim a number that is not there.
 */
export const isCalculatedColumnSource = (source: PaymentGridSource | undefined): boolean =>
    source === PaymentGridSource.UserProgramQuotes;

export type PaymentGridColumnResolution = {
    termMonths: number | null;
    source: PaymentGridSource;
};

export const appliesToTerm = <T extends { term?: number | null }>(
    entry: T | undefined,
    term: number | null | undefined
): entry is T => entry != null && (entry.term == null || entry.term === term);

export const resolveDealColumnIndex = (
    gridTerms: (number | null)[] | undefined,
    stampedColumnIndex: number | undefined,
    dealTermMonths: number | undefined
): number | undefined => {
    if (dealTermMonths == null) return undefined;
    const terms = gridTerms ?? [];
    if (stampedColumnIndex != null && terms[stampedColumnIndex] === dealTermMonths) return stampedColumnIndex;
    const firstHolder = terms.indexOf(dealTermMonths);
    return firstHolder === -1 ? undefined : firstHolder;
};

export const resolveTermOwnerColumnIndex = (
    gridTerms: (number | null)[] | undefined,
    stampedColumnIndex: number | undefined,
    dealTermMonths: number | undefined
): number | undefined => {
    const dealColumnIndex = resolveDealColumnIndex(gridTerms, stampedColumnIndex, dealTermMonths);
    if (dealColumnIndex != null || dealTermMonths == null || stampedColumnIndex == null) return dealColumnIndex;
    return gridTerms?.[stampedColumnIndex] != null ? stampedColumnIndex : undefined;
};

export const shouldSkipTermOverrideForColumn = (
    gridTerms: (number | null)[] | undefined,
    selectedColumnIndex: number | undefined,
    termMonths: number | undefined,
    editedColumnIndex: number | undefined,
    selectedTermMonths?: number
): boolean => {
    if (termMonths == null || editedColumnIndex == null) return false;

    const terms = gridTerms ?? [];
    if (terms.filter((gridTerm) => gridTerm === termMonths).length < 2) return false;

    const dealColumnIndex =
        termMonths === selectedTermMonths ? resolveDealColumnIndex(terms, selectedColumnIndex, selectedTermMonths) : undefined;

    return editedColumnIndex !== dealColumnIndex;
};

export const resolveByColumn = <T>(hasColumnInfo: boolean, columnAnswer: () => T, termAnswer: () => T): T =>
    hasColumnInfo ? columnAnswer() : termAnswer();

export const resolveColumnVerdict = (
    decidedColumns: Record<number, PaymentGridSource> | undefined,
    columnSources: Record<number, PaymentGridSource> | undefined,
    columnIndex: number,
    termMonths: number | null | undefined
): PaymentGridSource | undefined => decidedColumns?.[columnIndex] ?? (termMonths ? columnSources?.[termMonths] : undefined);

export const resolveCellVerdict = (
    gridTerms: (number | null)[],
    decidedColumns: Record<number, PaymentGridSource> | undefined,
    columnSources: Record<number, PaymentGridSource> | undefined,
    columnIndex: number | undefined,
    termMonths: number
): PaymentGridSource | undefined => {
    const termVerdict = columnSources?.[termMonths];
    const isHolderOfSharedTerm =
        columnIndex != null &&
        gridTerms[columnIndex] === termMonths &&
        gridTerms.filter((gridTerm) => gridTerm === termMonths).length > 1;
    return isHolderOfSharedTerm ? decidedColumns?.[columnIndex as number] ?? termVerdict : termVerdict;
};

export type ResolveColumnSourcesArgs = {
    termMonthsList: (number | null)[];
    downPaymentList: (number | null)[];
    hasProgramForTerm: (termMonths: number) => boolean;
    hasQuoteForCell: (termMonths: number, downPayment: number) => boolean;
};

/** A column with no term is empty and user-fillable, not a failure. */
export const hasTerm = (termMonths: number | null): termMonths is number => typeof termMonths === 'number' && termMonths > 0;

export const validDownPayments = (downPaymentList: (number | null)[]): number[] =>
    downPaymentList.filter((downPayment): downPayment is number => typeof downPayment === 'number');

/** Resolves every column to a single source: program-backed only when a program exists AND all its cells priced, so one column never mixes program-backed and estimated payments. */
export const resolveColumnSources = ({
    termMonthsList,
    downPaymentList,
    hasProgramForTerm,
    hasQuoteForCell
}: ResolveColumnSourcesArgs): PaymentGridColumnResolution[] => {
    const downPayments = validDownPayments(downPaymentList);

    return termMonthsList.map((termMonths) => {
        const isProgramBacked =
            hasTerm(termMonths) &&
            hasProgramForTerm(termMonths) &&
            downPayments.every((downPayment) => hasQuoteForCell(termMonths, downPayment));

        return {
            termMonths,
            source: isProgramBacked ? PaymentGridSource.ProgramQuotes : PaymentGridSource.UserProgramQuotes
        };
    });
};

/** The columns needing a fallback pass. Columns with no term are excluded - awaiting input, not a failure. */
export const getFallbackTerms = (resolutions: PaymentGridColumnResolution[]): number[] =>
    resolutions
        .filter((resolution) => resolution.source === PaymentGridSource.UserProgramQuotes && hasTerm(resolution.termMonths))
        .map((resolution) => resolution.termMonths as number);

export type SplitTermsByEngineArgs = {
    termList: number[];
    columnSources: Record<number, PaymentGridSource>;
    dealIsUserProgramQuotes: boolean;
    isTargetedColumnEdit?: boolean;
};

export type TermsByEngine = {
    priceableTerms: number[];
    userProgramQuotesTerms: number[];
    programQuotesTerms: number[];
    isRowSplit: boolean;
};

export const isUserProgramQuotesColumn = (source: PaymentGridSource | undefined, dealIsUserProgramQuotes: boolean): boolean => {
    if (source === PaymentGridSource.UserProgramQuotes || source === PaymentGridSource.Unavailable) return true;
    if (source === PaymentGridSource.ProgramQuotes) return false;
    return dealIsUserProgramQuotes;
};

export type PaymentGridColumnVerdicts = {
    terms?: (number | null)[];
    decidedColumns?: Record<number, PaymentGridSource>;
    columnSources?: Record<number, PaymentGridSource>;
};

export const keepsColumnResidualOnMileageChange = (
    gridData: PaymentGridColumnVerdicts | undefined,
    columnIndex: number,
    dealIsUserProgramQuotes: boolean
): boolean =>
    isUserProgramQuotesColumn(
        resolveColumnVerdict(gridData?.decidedColumns, gridData?.columnSources, columnIndex, gridData?.terms?.[columnIndex]),
        dealIsUserProgramQuotes
    );

export const keepsTermResidualOnMileageChange = (
    gridData: PaymentGridColumnVerdicts | undefined,
    termMonths: number,
    dealIsUserProgramQuotes: boolean
): boolean => {
    const holders = (gridData?.terms ?? []).flatMap((term, columnIndex) => (term === termMonths ? [columnIndex] : []));
    return holders.length
        ? holders.every((columnIndex) => keepsColumnResidualOnMileageChange(gridData, columnIndex, dealIsUserProgramQuotes))
        : dealIsUserProgramQuotes;
};

export const editGoesOutOnUserProgramQuotes = (
    source: PaymentGridSource | undefined,
    dealIsUserProgramQuotes: boolean,
    programBackedEditsStay = false
): boolean =>
    programBackedEditsStay ? isUserProgramQuotesColumn(source, dealIsUserProgramQuotes) : source != null || dealIsUserProgramQuotes;

/**
 * Splits one row's terms by engine, since a request carries only one. Three rules: `unavailable` is dropped
 * from a bulk update but kept for a targeted column edit, which is its retry path; `unavailable` groups with
 * user-program-quotes, as program-quotes cannot be steered by a typed rate; an undecided column follows the
 * deal's own endpoint, so a grid opened with ASP off never attempts program-quotes.
 *
 * The retry needs the column to be priceable, not merely edited: on user-program-quotes a residual is required
 * alongside the rate, so a rate typed into a column with no residual is held upstream and never reaches this
 * split - see `shouldHoldGridEditForIncompleteColumn` (DE762937). A typed residual retries as it always did.
 */
export const splitTermsByEngine = ({
    termList,
    columnSources,
    dealIsUserProgramQuotes,
    isTargetedColumnEdit = false
}: SplitTermsByEngineArgs): TermsByEngine => {
    const priceableTerms = isTargetedColumnEdit
        ? termList
        : termList.filter((termMonths) => columnSources[termMonths] !== PaymentGridSource.Unavailable);

    const isUserProgramQuotesEngine = (termMonths: number): boolean =>
        isUserProgramQuotesColumn(columnSources[termMonths], dealIsUserProgramQuotes);

    const userProgramQuotesTerms = priceableTerms.filter(isUserProgramQuotesEngine);
    const programQuotesTerms = priceableTerms.filter((termMonths) => !isUserProgramQuotesEngine(termMonths));

    return {
        priceableTerms,
        userProgramQuotesTerms,
        programQuotesTerms,
        isRowSplit: Boolean(userProgramQuotesTerms.length) && Boolean(programQuotesTerms.length)
    };
};

export type IsGridCompleteArgs = {
    termMonthsList: (number | null)[];
    downPaymentList: (number | null)[];
    hasQuoteForCell: (termMonths: number, downPayment: number) => boolean;
    unavailableTerms?: number[];
};

/** Render gate: shown once every cell with a term holds a payment. Columns with no term, and columns whose fallback already failed, are excluded - neither will ever quote. */
export const isGridComplete = ({
    termMonthsList,
    downPaymentList,
    hasQuoteForCell,
    unavailableTerms = []
}: IsGridCompleteArgs): boolean => {
    const downPayments = validDownPayments(downPaymentList);
    const terms = termMonthsList.filter(hasTerm).filter((termMonths) => !unavailableTerms.includes(termMonths));

    // No axis means no cell could still be waiting, so both report complete: reporting "resolving" would hold the
    // grid with nothing in flight, and the cash-down list is briefly empty after RESET_PAYMENT_GRID.
    if (!downPayments.length) return true;
    if (!terms.length) return true;

    return terms.every((termMonths) => downPayments.every((downPayment) => hasQuoteForCell(termMonths, downPayment)));
};
